/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms/views/View'
import { mono, boFont, boMotion } from './backoffice.tokens'

export interface BoBarRow {
	label: React.ReactNode
	value: number
	/** 행마다 다른 기준(예: 시즌 수량). 없으면 props.max → 행 최댓값 */
	max?: number
	/** 값 옆 작은 글 — `12 / 40` */
	note?: React.ReactNode
	onClick?: () => void
}

interface PropsType {
	rows: BoBarRow[]
	max?: number
	format?: (n: number) => string
	/** 왼쪽 위 키커(대문자 자간) */
	caption?: string
	/** 키커 아래 한 줄 — 제품명·시즌명처럼 대문자로 바꾸면 안 되는 글 */
	subcaption?: React.ReactNode
	emptyText?: string
	className?: string
}

/**
 * 순위·비율 가로 막대 — 라벨 | 트랙 | 값. 로그 대시보드의 「국가별 TOP 10」 줄을 원시 컴포넌트로 뺐다.
 * **값 칸은 그 목록에서 가장 긴 글자 수로 고정한다**(2026-09-29) — 행마다 제 폭을 잡으면 값이 한 자리 늘 때마다
 * 트랙 오른쪽 끝이 밀려 한 열 안에서 막대가 어긋난다. 폭은 `--boBarValueW`(ch) 로 내려보내 모든 행이 같이 쓴다.
 * 막대는 ink 한 색(모노크롬), 트랙은 헤어라인 색. 숫자는 항상 글자로 옆에 선다(색·길이만으로 읽게 하지 않는다).
 */
export const BoBars = (props: PropsType) => {
	const format = props.format ?? ((n: number) => n.toLocaleString('en-US'))
	const rowMax = Math.max(1, props.max ?? 0, ...props.rows.map(r => r.value))

	/**
	 * 값 칸 폭 — 가장 긴 `값 + 덧글` 의 글자 수. 숫자는 `tabular-nums` 라 1ch 가 정확하고,
	 * 덧글(11px)은 값(13px)보다 작아 0.85 로 세고 사이 여백 0.6 을 더한다. 덧글이 글자가 아니면 못 세므로 건너뛴다.
	 */
	const valueChars = (r: BoBarRow): number => {
		const note =
			typeof r.note === 'string' || typeof r.note === 'number' ? String(r.note) : ''
		return format(r.value).length + (note === '' ? 0 : note.length * 0.85 + 0.6)
	}
	const valueW = `${Math.ceil(Math.max(3, ...props.rows.map(valueChars)) * 10) / 10}ch`

	return (
		<View
			css={style}
			className={`boBars ${props.className ?? ''}`}
			style={{ ['--boBarValueW']: valueW } as React.CSSProperties}
		>
			{props.caption !== undefined ? (
				<span className="boBarsCaption">{props.caption}</span>
			) : null}
			{props.subcaption !== undefined ? (
				<span className="boBarsSub">{props.subcaption}</span>
			) : null}
			{props.rows.length === 0 ? (
				<p className="boBarsEmpty">{props.emptyText ?? '데이터 없음'}</p>
			) : (
				props.rows.map((r, i) => {
					const base = Math.max(1, r.max ?? rowMax)
					const pct = Math.min(100, Math.max(0, (r.value / base) * 100))
					const Tag: any = r.onClick !== undefined ? 'button' : 'div'
					return (
						<Tag
							key={i}
							type={r.onClick !== undefined ? 'button' : undefined}
							className={`boBarRow ${r.onClick !== undefined ? 'clickable' : ''}`}
							onClick={r.onClick}
						>
							<span className="boBarLabel">{r.label}</span>
							<span className="boBarTrack" aria-hidden="true">
								<span className="boBarFill" style={{ width: `${pct}%` }} />
							</span>
							<span className="boBarValue">
								{format(r.value)}
								{r.note !== undefined ? (
									<span className="boBarNote">{r.note}</span>
								) : null}
							</span>
						</Tag>
					)
				})
			)}
		</View>
	)
}

const style = css({
	display: 'flex',
	flexDirection: 'column',
	minWidth: 0,
	fontFamily: boFont.ui,

	'.boBarsCaption': {
		fontSize: 11,
		letterSpacing: '.18em',
		textTransform: 'uppercase',
		fontWeight: 500,
		color: mono.faint,
		marginBottom: 6,
	},
	'.boBarsSub': { fontSize: 12, color: mono.dim, marginBottom: 8, marginTop: -2 },
	'.boBarRow': {
		display: 'grid',
		// 값 칸은 계산한 폭으로 고정하되, 못 센 덧글(문자열이 아닌 것)이 더 길면 그만큼만 늘어난다
		gridTemplateColumns:
			'minmax(0, 1fr) minmax(64px, 140px) minmax(var(--boBarValueW, auto), max-content)',
		alignItems: 'center',
		gap: 14,
		minHeight: 34,
		padding: '4px 0',
		borderBottom: `1px solid ${mono.line}`,
		background: 'transparent',
		border: 0,
		borderBottomWidth: 1,
		borderBottomStyle: 'solid',
		borderBottomColor: mono.line,
		textAlign: 'left',
		font: 'inherit',
		color: 'inherit',
		width: '100%',
		transition: boMotion,
		'&:last-of-type': { borderBottom: 0 },
		'&.clickable': { cursor: 'pointer' },
		'&.clickable:hover': { background: mono.bg },
		'&.clickable:focus-visible': { outline: `1px solid ${mono.ink}`, outlineOffset: 2 },
	},
	'.boBarLabel': {
		fontSize: 13,
		fontWeight: 400,
		color: mono.ink,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	'.boBarTrack': {
		display: 'block',
		height: 6,
		background: mono.line,
		borderRadius: 1,
		overflow: 'hidden',
	},
	'.boBarFill': {
		display: 'block',
		height: '100%',
		background: mono.ink,
		opacity: 0.85,
		transition: 'width 200ms ease-out',
	},
	'.boBarValue': {
		fontSize: 13,
		fontVariantNumeric: 'tabular-nums',
		color: mono.ink,
		textAlign: 'right',
		whiteSpace: 'nowrap',
	},
	'.boBarNote': { marginLeft: 6, fontSize: 11, color: mono.faint },
	'.boBarsEmpty': {
		margin: 0,
		padding: '14px 0',
		fontSize: 13,
		fontWeight: 300,
		color: mono.faint,
	},
})
